import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tooltip,
  InputAdornment,
  Pagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/AssessmentOutlined';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import axios from 'axios';
import { Person, Task } from '../types';
import { format, differenceInDays } from 'date-fns';
import config from '../config';

const ITEMS_PER_PAGE = 20;

const PeopleManager = (): JSX.Element => {
  const [people, setPeople] = useState<Person[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [viewingPerson, setViewingPerson] = useState<Person | null>(null);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newRanking, setNewRanking] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [skillsSearch, setSkillsSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    fetchPeople();
    fetchTasks();
  }, []);

  const getAssignments = (assignments: any[], referenceDate: Date) => {
    const pastAssignments = assignments
      .filter((a) => new Date(a.startDate) <= referenceDate)
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    const upcomingAssignments = assignments
      .filter((a) => new Date(a.startDate) > referenceDate)
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

    return {
      lastAssignment: pastAssignments[0],
      nextAssignment: upcomingAssignments[0],
    };
  };

  const getRelativeDays = (date: string, referenceDate: Date) => {
    const days = differenceInDays(new Date(date), referenceDate);
    if (days < 0) {
      return `(${Math.abs(days)} days ago)`;
    } else if (days > 0) {
      return `(in ${days} days)`;
    } else {
      return '(today)';
    }
  };

  const fetchPeople = async () => {
    try {
      const response = await axios.get(`${config.api.baseUrl}/people`, {
        withCredentials: true,
      });
      setPeople(response.data);
    } catch (error) {
      console.error('Error fetching people:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${config.api.baseUrl}/tasks`, {
        withCredentials: true,
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleCreatePerson = async () => {
    if (!newFirstName.trim() || !newLastName.trim()) {
      setNameError('First and last name are required');
      return;
    }
    setNameError('');

    try {
      const response = await axios.post(
        `${config.api.baseUrl}/people`,
        {
          name: `${newFirstName.trim()} ${newLastName.trim()}`,
          email: newEmail || null,
          phone: newPhone || null,
          ranking: newRanking || null,
          notes: newNotes || null,
          skills: selectedSkills,
        },
        {
          withCredentials: true,
        },
      );
      setPeople([...people, response.data]);
      handleCloseDialog();
    } catch (error) {
      console.error('Error creating person:', error);
    }
  };

  const handleUpdatePerson = async () => {
    if (!editingPerson) return;

    if (!newFirstName.trim() || !newLastName.trim()) {
      setNameError('First and last name are required');
      return;
    }
    setNameError('');

    try {
      const response = await axios.put(
        `${config.api.baseUrl}/people/${editingPerson.id}`,
        {
          name: `${newFirstName.trim()} ${newLastName.trim()}`,
          email: newEmail || null,
          phone: newPhone || null,
          ranking: newRanking || null,
          notes: newNotes || null,
          skills: selectedSkills,
        },
        {
          withCredentials: true,
        },
      );

      setPeople(people.map((p) => (p.id === editingPerson.id ? response.data : p)));
      handleCloseDialog();
    } catch (error) {
      console.error('Error updating person:', error);
    }
  };

  const handleOpenDialog = (person?: Person) => {
    if (person) {
      setEditingPerson(person);
      const nameParts = person.name.trim().split(' ');
      setNewFirstName(nameParts.slice(0, -1).join(' '));
      setNewLastName(nameParts.length > 1 ? nameParts[nameParts.length - 1] : '');
      setNewEmail(person.email || '');
      setNewPhone(person.phone || '');
      setNewRanking(person.ranking || '');
      setNewNotes(person.notes || '');
      setSelectedSkills(person.varos?.map((pv) => pv.varosId) || []);
    } else {
      setEditingPerson(null);
      setNewFirstName('');
      setNewLastName('');
      setNewEmail('');
      setNewPhone('');
      setNewRanking('');
      setNewNotes('');
      setSelectedSkills([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPerson(null);
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setNewPhone('');
    setNewRanking('');
    setNewNotes('');
    setSelectedSkills([]);
    setNameError('');
  };

  const handleOpenViewDialog = (person: Person) => {
    setViewingPerson(person);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingPerson(null);
  };

  const handleSkillChange = (event: SelectChangeEvent<number[]>) => {
    setSelectedSkills(event.target.value as number[]);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM d, yyyy');
  };

  const filteredPeople = people
    .filter((person) => {
      const matchesSearch =
        searchQuery === '' ||
        person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (person.ranking && person.ranking.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (person.notes && person.notes.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesSkills =
        skillsSearch === '' ||
        (person.varos &&
          person.varos.some((pv) => {
            const skillName = pv.varos?.name || '';
            const skillDescription = pv.varos?.description || '';
            const searchTerm = skillsSearch.toLowerCase();
            return (
              skillName.toLowerCase().includes(searchTerm) ||
              skillDescription.toLowerCase().includes(searchTerm)
            );
          }));

      return matchesSearch && matchesSkills;
    })
    .map((person) => {
      const nameParts = person.name.trim().split(' ');
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
      const firstName = nameParts.slice(0, -1).join(' ');
      return {
        ...person,
        displayName: firstName ? `${lastName}, ${firstName}` : lastName,
        _sortLastName: lastName.toLowerCase(),
      };
    })
    .sort((a, b) => {
      const lastNameComparison = a._sortLastName.localeCompare(b._sortLastName);
      if (lastNameComparison !== 0) return lastNameComparison;
      return a.displayName.localeCompare(b.displayName);
    });

  const totalPages = Math.ceil(filteredPeople.length / ITEMS_PER_PAGE);
  const paginatedPeople = filteredPeople.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, skillsSearch]);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Murids
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexGrow: 1 }}>
          <TextField
            fullWidth
            placeholder="Search by name, ranking, or notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            placeholder="Search by skills..."
            value={skillsSearch}
            onChange={(e) => setSkillsSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add New Murid
        </Button>
      </Box>

      <Paper>
        <List>
          {paginatedPeople.map((person) => (
            <ListItem key={person.id}>
              <ListItemText
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{person.displayName}</span>
                    {person.ranking && (
                      <Chip label={person.ranking} size="small" color="secondary" />
                    )}
                  </Box>
                }
                secondary={
                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                      {person.email && <span>{person.email}</span>}
                      {person.phone && <span>{person.phone}</span>}
                    </Box>
                    <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {person.varos?.map((pv) => (
                        <Chip
                          key={pv.id}
                          label={pv.varos.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    {person.notes && (
                      <span style={{ color: 'text.secondary' }}>
                        {person.notes}
                      </span>
                    )}
                    {person.assignments && person.assignments.length > 0 && (
                      <Box component="span" sx={{ mt: 1 }}>
                        {(() => {
                          const { lastAssignment, nextAssignment } = getAssignments(
                            person.assignments,
                            selectedDate
                          );
                          return (
                            <>
                              {lastAssignment && (
                                <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
                                  Last Assignment {getRelativeDays(lastAssignment.startDate, selectedDate)}:
                                  <Tooltip
                                    title={`${formatDate(lastAssignment.startDate)}`}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}
                                    >
                                      {lastAssignment.varos.name}
                                      {lastAssignment.special && (
                                        <StarIcon
                                          sx={{ fontSize: 16, ml: 0.5, color: 'primary.main' }}
                                        />
                                      )}
                                    </Box>
                                  </Tooltip>
                                </Box>
                              )}
                              {nextAssignment && (
                                <Box component="span" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                  Upcoming Assignment {getRelativeDays(nextAssignment.startDate, selectedDate)}:
                                  <Tooltip
                                    title={`${formatDate(nextAssignment.startDate)}`}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}
                                    >
                                      {nextAssignment.varos.name}
                                      {nextAssignment.special && (
                                        <StarIcon
                                          sx={{ fontSize: 16, ml: 0.5, color: 'primary.main' }}
                                        />
                                      )}
                                    </Box>
                                  </Tooltip>
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleOpenViewDialog(person)}>
                  <VisibilityIcon />
                </IconButton>
                <IconButton edge="end" onClick={() => handleOpenDialog(person)}>
                  <EditIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPerson ? 'Edit Person' : 'Add New Murid'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Name
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                autoFocus
                margin="dense"
                placeholder="First"
                value={newFirstName}
                onChange={e => {
                  setNewFirstName(e.target.value);
                  if (e.target.value.trim() && newLastName.trim()) {
                    setNameError('');
                  }
                }}
                error={!!nameError && !newFirstName.trim()}
                required
                fullWidth
                size="small"
              />
              <TextField
                margin="dense"
                placeholder="Last"
                value={newLastName}
                onChange={e => {
                  setNewLastName(e.target.value);
                  if (e.target.value.trim() && newFirstName.trim()) {
                    setNameError('');
                  }
                }}
                error={!!nameError && !newLastName.trim()}
                required
                fullWidth
                size="small"
              />
            </Stack>
            {nameError && (
              <Typography color="error" variant="caption">{nameError}</Typography>
            )}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="skills-label">Skills</InputLabel>
              <Select
                labelId="skills-label"
                label="Skills"
                multiple
                value={selectedSkills}
                onChange={handleSkillChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={tasks.find((t) => t.id === value)?.name}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {tasks.map((task) => (
                  <MenuItem key={task.id} value={task.id}>
                    {task.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Ranking"
              value={newRanking}
              onChange={(e) => setNewRanking(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Notes"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={editingPerson ? handleUpdatePerson : handleCreatePerson}
            variant="contained"
            color="primary"
          >
            {editingPerson ? 'Save Changes' : 'Create Murid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Person Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewingPerson ? `Person Details - ${viewingPerson.name}` : 'Person Details'}
        </DialogTitle>
        <DialogContent>
          {viewingPerson && (
            <Box sx={{ mt: 2 }}>
              {/* Personal Information Section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Personal Information
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Name:</strong> {viewingPerson.name}
                </Typography>
                {viewingPerson.email && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {viewingPerson.email}
                  </Typography>
                )}
                {viewingPerson.phone && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Phone:</strong> {viewingPerson.phone}
                  </Typography>
                )}
                {viewingPerson.ranking && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Ranking:</strong> <Chip label={viewingPerson.ranking} size="small" color="secondary" />
                  </Typography>
                )}
                {viewingPerson.notes && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Notes:</strong> {viewingPerson.notes}
                  </Typography>
                )}
                {viewingPerson.varos && viewingPerson.varos.length > 0 && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Skills:</strong>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {viewingPerson.varos.map((pv) => (
                        <Chip
                          key={pv.id}
                          label={pv.varos.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Typography>
                )}
              </Box>

              {/* Assignment History Section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                Varo Assignment History
              </Typography>
              {viewingPerson.assignments && viewingPerson.assignments.length > 0 ? (
                <Box>
                  {viewingPerson.assignments
                    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                    .map((assignment) => (
                      <Box
                        key={assignment.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 100 }}>
                          {formatDate(assignment.startDate)}
                        </Typography>
                        <Typography variant="body1" sx={{ flexGrow: 1 }}>
                          {assignment.varos.name}
                        </Typography>
                        {assignment.special && (
                          <StarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        )}
                      </Box>
                    ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No assignment history available.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PeopleManager;
